import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PosterCard from "../components/PosterCard";
import WheelBrowser, { cardRow } from "../components/clickwheel/WheelBrowser";
import { useTheme } from "../context/ThemeContext";
import Disclaimer from "../components/Disclaimer";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GENRES, genreName } from "../lib/genres";

const PROVIDERS = [
  { id: "netflix", name: "Netflix" },
  { id: "hulu", name: "Hulu" },
  { id: "prime", name: "Prime Video" },
  { id: "max", name: "Max" },
  { id: "paramount", name: "Paramount+" },
  { id: "appletv", name: "Apple TV+" },
  { id: "disney", name: "Disney+" },
  { id: "peacock", name: "Peacock" },
  { id: "youtubetv", name: "YouTube TV" },
];

export default function GenreBrowse() {
  const wheel = useTheme().skin === "clickwheel";
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const hasSubs = (user?.services || []).length > 0;
  // mode: "mine" (user's subscriptions, default) | "all" | a single service id
  const [mode, setMode] = useState(hasSubs ? "mine" : "all");
  const [mediaType, setMediaType] = useState("all"); // all | movie | tv

  // Reset filters to defaults whenever the genre changes.
  useEffect(() => { setMode(hasSubs ? "mine" : "all"); setMediaType("all"); /* eslint-disable-next-line */ }, [slug]);

  const subsKey = (user?.services || []).join(",");
  useEffect(() => {
    let alive = true;
    setItems(null);
    let providers = "";
    if (mode === "mine") providers = (user?.services || []).join(",");
    else if (mode !== "all") providers = mode;
    const qs = new URLSearchParams({ providers, media: mediaType });
    api.get(`/catalog/genre/${slug}?${qs.toString()}`)
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [slug, mode, mediaType, subsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = genreName(slug);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="px-4 lg:px-14 pt-6 pb-20" data-testid="genre-browse-page" data-genre={slug}>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--accent-hover)]">Browse Genre</p>
            <h1 className="font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)]" data-testid="genre-title">{name}</h1>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <span className="hidden sm:inline">Jump to genre</span>
            <select
              value={slug}
              onChange={(e) => navigate(`/genre/${e.target.value}`)}
              data-testid="genre-switcher"
              className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
            >
              {GENRES.map((g) => <option key={g.slug} value={g.slug}>{g.name}</option>)}
            </select>
          </label>
        </div>

        {/* Media-type toggle */}
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="genre-media-filters">
          <span className="mr-1 text-xs font-semibold uppercase tracking-widest text-[var(--text-muted)]">Type</span>
          <FilterChip label="All" active={mediaType === "all"} onClick={() => setMediaType("all")} testid="genre-media-all" />
          <FilterChip label="Movies" active={mediaType === "movie"} onClick={() => setMediaType("movie")} testid="genre-media-movie" />
          <FilterChip label="TV Shows" active={mediaType === "tv"} onClick={() => setMediaType("tv")} testid="genre-media-tv" />
        </div>

        {/* Provider filter chips */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="genre-provider-filters">
          {hasSubs && <FilterChip label="My Services" active={mode === "mine"} onClick={() => setMode("mine")} testid="genre-filter-mine" />}
          <FilterChip label="All" active={mode === "all"} onClick={() => setMode("all")} testid="genre-filter-all" />
          {PROVIDERS.map((p) => (
            <FilterChip
              key={p.id}
              label={p.name}
              active={mode === p.id}
              subscribed={(user?.services || []).includes(p.id)}
              onClick={() => setMode(p.id)}
              testid={`genre-filter-${p.id}`}
            />
          ))}
        </div>

        {items === null && <p className="mt-10 text-[var(--text-secondary)]" data-testid="genre-loading">Loading {name} titles…</p>}

        {items && items.length === 0 && (
          <div className="mt-16 text-center text-[var(--text-secondary)]" data-testid="genre-empty">
            No {name} titles found{mode !== "all" ? " for this filter — try “All”." : "."}
          </div>
        )}

        {items && items.length > 0 && wheel && <WheelBrowser rows={items.map((it) => cardRow(it))} title={genreName(slug) || "Genre"} testid="wheel-genre" />}
        {items && items.length > 0 && !wheel && (
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="genre-results-grid">
            {items.map((it) => (
              <div key={it.id} className="w-full [&>div]:w-full">
                <PosterCard item={it} />
              </div>
            ))}
          </div>
        )}
      </main>
      <Disclaimer />
    </div>
  );
}

export function FilterChip({ label, active, onClick, testid, subscribed }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`genre-pill ${active ? "genre-pill-active" : ""}`}>
      {label}
      {subscribed && !active && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />}
    </button>
  );
}
